// https://pspdfkit.com/api/web/
// https://github.com/PSPDFKit/awesome-pspdfkit
// make sure you have the PSPDFKit license key(VITE_PSPDFKIT_LICENSE_KEY) in your .env file

import { useEffect, useRef, useState } from 'react';

import type { Configuration, Instance, ToolbarItem } from 'pspdfkit';

import { config } from 'config';

type PDFViewerProps = {
  url: string;
  readOnly?: boolean; // This prop will default to `true`.
  toolbarItems?: ToolbarItem[]; // This is a list of items to override the default toolbar items`.
  configurationsOptions?: Configuration;
};

export default function PSPDFKitViewer({
  url,
  readOnly = true,
  toolbarItems,
  configurationsOptions,
}: PDFViewerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState<Error | null>(null);

  useEffect(() => {
    const container = containerRef.current; // This `useRef` instance will render the PDF.
    if (!container) return;

    let instance: Instance | undefined;
    let PSPDFKit:
      | Awaited<typeof import('pspdfkit')>['default']
      | undefined;
    let cancelled = false;

    const baseUrl =
      config.environment === 'development'
        ? `${window.location.origin}/public/`
        : `${window.location.origin}/`;

    setIsLoading(true);
    setLoadError(null);

    (async function () {
      try {
        PSPDFKit = (await import('pspdfkit')).default;
        if (cancelled) return;

        PSPDFKit.unload(container); // Ensure that there's only one PSPDFKit instance.

        // https://pspdfkit.com/api/web/PSPDFKit.Configuration.html
        instance = await PSPDFKit.load({
          licenseKey: config.psPdfKit.licenseKey,
          container,
          document: url,
          baseUrl,
          ...configurationsOptions,
        });

        if (cancelled) {
          PSPDFKit.unload(container);
          return;
        }

        // https://pspdfkit.com/api/web/PSPDFKit.ViewState.html
        instance.setViewState((viewState) => viewState.set('readOnly', readOnly));

        // Set/remove/add custom toolbar items.
        // https://pspdfkit.com/api/web/PSPDFKit.Instance.html#setToolbarItems
        if (toolbarItems) {
          instance.setToolbarItems(toolbarItems);
        }

        setIsLoading(false);
      } catch (error) {
        if (cancelled) return;

        console.error('PSPDFKit failed to load:', error);
        setLoadError(
          error instanceof Error ? error : new Error('PDF failed to load')
        );
        setIsLoading(false);
      }
    })();
    // cleanup the PSPDFKit instance
    return () => {
      cancelled = true;
      if (instance || PSPDFKit) {
        PSPDFKit?.unload(container);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configurationsOptions, readOnly, toolbarItems, url]);

  // This div element will render the document to the DOM.
  return (
    <div style={{ width: '100%', height: 'inherit', position: 'relative' }}>
      {isLoading && <div style={{ padding: 16 }}>Loading PDF...</div>}
      {loadError && (
        <div role="alert" style={{ padding: 16 }}>
          Could not load PDF preview. Please try again or download the file.
        </div>
      )}
      <div ref={containerRef} style={{ width: '100%', height: 'inherit' }} />
    </div>
  );
}
